import moment from 'moment'
import { useEffect, useState } from 'react'
import {
    TRIGGERED_ID,
    getEventTypeListItems
} from '../../locations/partials/constants'
import { isSelectedConnectivity } from '../../locations/utils/connectivity-heatmap'
import { useQuery } from 'react-query'
import { getLastReportDate } from '../../locations/api/locations-api'
import { useDispatch, useSelector } from 'react-redux'
import { enqueueSnackbar } from '../actions'
import { getSagasMsgs } from '../selectors'

export const INIT_FLOOR_PLAN_START_DATE = moment().subtract(4, 'weeks')
const initDevicesFilters = []
const initHeatmapFilters = {
    startDate: INIT_FLOOR_PLAN_START_DATE,
    endDate: null,
    reportTypes: [{ id: TRIGGERED_ID, urlParamName: 'isTriggered' }]
}

export const useFloorPlanFilters = ({ locationId, isMobile = false }) => {
    const dispatch = useDispatch()
    const sagasMsgs = useSelector(getSagasMsgs)

    const [devicesFilters, setDevicesFilters] = useState(initDevicesFilters)
    const [heatmapFilters, setHeatmapFilters] = useState(initHeatmapFilters)
    const [totalHeatmapApplied, setTotalHeatmapApplied] = useState(0)
    const [totalFiltersApplied, setTotalFiltersApplied] = useState(0)

    const applyDeviceFilters = filters => setDevicesFilters([...filters])

    const applyHeatmapFilter = (filterName, filterValue) =>
        setHeatmapFilters({ ...heatmapFilters, [filterName]: filterValue })

    const applyMobileHeatmapFilters = (filters, locale) => {
        const { startDate, endDate, reportTypes } = filters
        setHeatmapFilters({
            startDate: startDate ? startDate * 1000 : null,
            endDate: endDate ? endDate * 1000 : null,
            reportTypes: getEventTypeListItems(locale).filter(x =>
                reportTypes?.includes(x.id)
            )
        })
    }

    const { data: lastReportDate } = useQuery(
        'last-report-date',
        () => getLastReportDate({ locationId: locationId }),
        {
            refetchInterval: false,
            refetchOnWindowFocus: false,
            onSuccess: data => {
                setHeatmapFilters({
                    ...heatmapFilters,
                    startDate: data.created_at
                })

                if (isMobile) {
                    window.ReactNativeWebView?.postMessage(
                        `init-heatmap-filters_${data.created_at}_${TRIGGERED_ID}`
                    )
                }
            },
            onError: () =>
                dispatch(
                    enqueueSnackbar({
                        message: sagasMsgs.getLastReportDateEx,
                        options: {
                            variant: 'error'
                        }
                    })
                )
        }
    )

    const calculateHeatmapTotalFiltersApplied = () => {
        const { startDate, endDate, reportTypes } = heatmapFilters

        if (isMobile) {
            return calculateHeatmapTotalFiltersAppliedMobile({
                startDate,
                endDate,
                reportTypes
            })
        }

        return calculateHeatmapTotalFiltersAppliedWeb({
            startDate,
            endDate,
            reportTypes
        })
    }

    const calculateHeatmapTotalFiltersAppliedMobile = ({
        startDate,
        endDate,
        reportTypes
    }) => {
        if (isSelectedConnectivity(reportTypes)) {
            return 1
        }

        return [startDate, endDate].filter(Boolean).length + reportTypes.length
    }

    const calculateHeatmapTotalFiltersAppliedWeb = ({
        startDate,
        endDate,
        reportTypes
    }) => {
        if (isSelectedConnectivity(reportTypes) || reportTypes.length === 0)
            return 0

        return [startDate, endDate].filter(Boolean).length
    }

    const buildHeatmapParams = ({ isMobile = false }) => {
        let params = {}
        if (heatmapFilters.startDate) {
            params.startDate = new Date(heatmapFilters.startDate)
        }

        if (heatmapFilters.endDate) {
            params.endDate = new Date(heatmapFilters.endDate)
        }

        let typesParams = {}
        if (heatmapFilters.reportTypes) {
            typesParams = heatmapFilters.reportTypes
                .filter(rt => !!rt.urlParamName)
                .reduce(
                    (acc, current) => ({
                        ...acc,
                        [current.urlParamName]: true
                    }),
                    {}
                )
        }
        return { ...params, ...typesParams }
    }

    const resetHeatmapFilters = () => {
        if (lastReportDate) {
            setHeatmapFilters({
                ...initHeatmapFilters,
                startDate: lastReportDate.created_at
            })
            return
        }

        setHeatmapFilters(initHeatmapFilters)
    }

    useEffect(() => {
        setTotalHeatmapApplied(calculateHeatmapTotalFiltersApplied())
        setTotalFiltersApplied(devicesFilters.length)
    }, [devicesFilters, heatmapFilters])

    return {
        totalFiltersApplied,
        totalHeatmapApplied,
        devicesFilters,
        applyDeviceFilters,
        applyMobileHeatmapFilters,
        heatmapFilters,
        applyHeatmapFilter,
        resetHeatmapFilters,
        buildHeatmapParams
    }
}
